import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { JsonViewer } from "@/components/common/JsonViewer";
import { Loader2, ShieldCheck } from "lucide-react";
import type { AuditLogListItem } from "@/types/admin";
import { useAuditLogDetail } from "@/hooks/useAuditQueries";
import { formatDateTime } from "@/lib/utils";
import {
  auditActionVariant,
  auditSeverityVariant,
  formatAuditAction,
  formatAuditCategory,
  isAdminAuditAction,
  parseAuditJson,
} from "@/lib/auditActions";

interface AuditLogDetailModalProps {
  /** The list row that was clicked. Used to render the header before the detail arrives. */
  log: AuditLogListItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Rendered in place of a value the row does not carry. */
const EMPTY = "—";

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-sm">
      <div className="text-muted-foreground">{label}</div>
      <div className="col-span-2 min-w-0 break-words">{value}</div>
    </div>
  );
}

function Mono({ value }: { value?: string | null }) {
  return value ? (
    <span className="font-mono text-xs select-all">{value}</span>
  ) : (
    <span className="text-muted-foreground">{EMPTY}</span>
  );
}

/**
 * Full view of one audit row.
 *
 * Fetches by ID rather than rendering the list row it was opened from: user agent, request ID and
 * the JSON state columns only exist on the detail response, and the modal used to show them as
 * blanks because it was handed a list row that never carried them.
 */
export function AuditLogDetailModal({
  log,
  open,
  onOpenChange,
}: AuditLogDetailModalProps) {
  const {
    data: detail,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useAuditLogDetail(open ? log.id : undefined);

  const metadata = parseAuditJson(detail?.metadata);
  const stateBefore = parseAuditJson(detail?.stateBefore);
  const stateAfter = parseAuditJson(detail?.stateAfter);
  const changes = parseAuditJson(detail?.changes);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Audit Log Details</DialogTitle>
        </DialogHeader>

        <DialogBody withoutFooter className="space-y-4">
          <DialogDescription>
            Complete information about this audit event
          </DialogDescription>
          {/* Straight from the list row, so the header is filled in before the fetch lands. */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={auditActionVariant(log.action)}>
              {formatAuditAction(log.action)}
            </Badge>
            {isAdminAuditAction(log.action) && (
              <Badge
                variant="outline"
                className="gap-1 border-primary/30 bg-primary/10 text-primary"
              >
                <ShieldCheck className="h-3 w-3" />
                Admin console
              </Badge>
            )}
            <Badge
              variant={auditSeverityVariant(log.severity)}
              className="capitalize"
            >
              {log.severity}
            </Badge>
            {log.category && (
              <span className="text-xs text-muted-foreground">
                {formatAuditCategory(log.category)}
              </span>
            )}
          </div>

          <p className="text-sm">{log.description || EMPTY}</p>

          <Separator />

          <div className="space-y-2">
            <div className="text-sm font-medium">Event</div>
            <Field label="Action code" value={<Mono value={log.action} />} />
            <Field label="Occurred at" value={formatDateTime(log.occurredAt)} />
            <Field label="Audit log ID" value={<Mono value={log.id} />} />
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="text-sm font-medium">Entity</div>
            <Field label="Type" value={log.entityType || EMPTY} />
            <Field label="ID" value={<Mono value={log.entityId} />} />
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="text-sm font-medium">Actor</div>
            <Field label="User" value={log.username || "System"} />
            <Field label="User ID" value={<Mono value={log.userId} />} />
            <Field
              label="Workspace"
              value={
                log.tenantName ||
                (log.tenantId ? EMPTY : "Platform (no workspace)")
              }
            />
            <Field label="Workspace ID" value={<Mono value={log.tenantId} />} />
          </div>

          <Separator />

          {/* Everything below needs the detail response. */}
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
              <p className="text-sm text-destructive-ink">
                Failed to load the full record: {error.message}
              </p>
              <Button
                onClick={() => void refetch()}
                variant="outline"
                size="sm"
                disabled={isFetching}
                className="h-7 shrink-0 px-2 text-xs"
              >
                {isFetching && (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                )}
                Retry
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <div className="text-sm font-medium">Request</div>
                <Field
                  label="IP address"
                  value={<Mono value={detail?.ipAddress} />}
                />
                <Field
                  label="User agent"
                  value={
                    detail?.userAgent ? (
                      <span className="text-xs break-all">
                        {detail.userAgent}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{EMPTY}</span>
                    )
                  }
                />
                <Field
                  label="Request ID"
                  value={<Mono value={detail?.requestId} />}
                />
              </div>

              {/* Each JSON section is hidden when empty rather than showing "{}": most actions
                  record metadata only, and only a few record a before/after state at all. */}
              {metadata !== null && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Metadata</div>
                    <JsonViewer
                      value={metadata}
                      maxHeightClassName="max-h-60"
                    />
                  </div>
                </>
              )}

              {stateBefore !== null && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <div className="text-sm font-medium">State before</div>
                    <JsonViewer
                      value={stateBefore}
                      maxHeightClassName="max-h-60"
                    />
                  </div>
                </>
              )}

              {stateAfter !== null && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <div className="text-sm font-medium">State after</div>
                    <JsonViewer
                      value={stateAfter}
                      maxHeightClassName="max-h-60"
                    />
                  </div>
                </>
              )}

              {/* Legacy column, superseded by the two above and null on everything written since.
                  Kept so historical rows still render what they recorded. */}
              {changes !== null && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Changes (legacy)</div>
                    <JsonViewer value={changes} maxHeightClassName="max-h-60" />
                  </div>
                </>
              )}
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
